'use client';

import { useEffect, useState } from 'react';
import styles from './RunningProcessModal.module.css';
import { useTheme } from '../context/ThemeProvider';

type Props = {
  isVisible: boolean;
  onComplete: () => void;
  messages: string[];
  progress?: number;
  showSuccess?: boolean;
};

export default function RunningProcessModal({ isVisible, onComplete, messages, progress: externalProgress, showSuccess }: Props) {
  const [progress, setProgress] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [showMessage, setShowMessage] = useState(true);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Use external progress if provided, otherwise use internal progress
  // If external progress is > 1, treat it as percentage (0-100), otherwise as decimal (0-1)
  const normalizedExternalProgress = externalProgress !== undefined
    ? (externalProgress > 1 ? externalProgress / 100 : externalProgress)
    : undefined;
  const currentProgress = normalizedExternalProgress !== undefined ? normalizedExternalProgress : progress;

  useEffect(() => {
    if (!isVisible) return;

    const timers: NodeJS.Timeout[] = [];

    // Only use internal progress if no external progress is provided
    if (externalProgress === undefined) {
      const progressTimer = setInterval(() => {
        setProgress(prev => (prev < 0.8 ? prev + 0.01 : prev));
      }, 50);
      timers.push(progressTimer);
    }

    // Message timer for all cases
    const messageTimer = setInterval(() => {
      setShowMessage(false);
      setTimeout(() => {
        setCurrentMessageIndex(i => (i + 1) % messages.length);
        setShowMessage(true);
      }, 300);
    }, 3500);
    timers.push(messageTimer);

    return () => {
      timers.forEach(timer => clearInterval(timer));
    };
  }, [isVisible, externalProgress, messages.length]);

  useEffect(() => {
    if (!isVisible) return;

    // Handle completion based on progress or success state
    if (showSuccess || (externalProgress === undefined && progress >= 0.8)) {
      const timer = setTimeout(() => {
        if (externalProgress !== undefined) {
          setProgress(1);
        } else {
          setProgress(1);
        }
        setTimeout(onComplete, 700);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [progress, isVisible, onComplete, externalProgress, showSuccess]);

  if (!isVisible) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Video Animation */}
        <div className={styles.animation} style={{ mixBlendMode: isDarkMode ? "plus-lighter" : "difference" }}>
          <video
            poster="https://i.pinimg.com/videos/thumbnails/originals/37/a7/39/37a7390a12291015429da39931bf1e58.0000000.jpg"
            preload="auto"
            autoPlay
            muted
            loop
            playsInline
            src="/assets/videos/processing-animation-03.mp4"
          />
        </div>

        {/* Rotating Message Text */}
        <div className={styles.messageContainer}>
          {messages.length > 0 && messages[currentMessageIndex] ? (
            <div
              key={currentMessageIndex}
              className={styles.messageText}
              style={{ opacity: showMessage ? 1 : 0, transition: 'opacity 0.3s ease-in-out' }}
            >
              {messages[currentMessageIndex]}
            </div>
          ) : (
            <div className={styles.messageText} style={{ opacity: 0 }}>
              Loading...
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className={styles.progressWrapper}>
          <div className={styles.progressBar} style={{ width: `${(currentProgress || 0) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}